import { SharedCommonUtility } from '../utils/common.utility';
import { SharedObjectUtility } from '../utils/object.utility';
import { IFunctionalAreaHierarchy } from '../interfaces/security-role.interface';
import { FunctionalArea } from './security';

export const FunctionalAreaHierarchy: IFunctionalAreaHierarchy = SharedCommonUtility.freezeDeep<IFunctionalAreaHierarchy>({
  [FunctionalArea.application]: {
    [FunctionalArea.pdf]: {
      [FunctionalArea.pdf_testing]: {},
      [FunctionalArea.pdf_remediation]: {},
      [FunctionalArea.pdf_administration]: {},
    },
    [FunctionalArea.governance_and_reporting]: {
      [FunctionalArea.gr_dashboard]: {},
      [FunctionalArea.gr_governance]: {},
      [FunctionalArea.gr_monitoring]: {},
      [FunctionalArea.gr_organization_dashboard]: {},
      [FunctionalArea.summary_snapshot_api]: {},
    },
    [FunctionalArea.digital_asset_findings]: {},
    [FunctionalArea.design_evaluations]: {
      [FunctionalArea.de_administration]: {},
      [FunctionalArea.de_results]: {},
    },
    [FunctionalArea.tools_and_integrations]: {
      [FunctionalArea.ti_tools_and_extensions]: {},
      [FunctionalArea.te_test_automation]: {},
      [FunctionalArea.te_tools_and_extensions_testing]: {},
      [FunctionalArea.te_document_accessibility_remediation]: {},
      [FunctionalArea.ti_issue_tracking_basic]: {},
      [FunctionalArea.ti_issue_tracking_administration]: {},
      [FunctionalArea.ti_issue_tracking_configuration]: {},
      [FunctionalArea.ti_scim_config]: {},
      [FunctionalArea.ti_leveldocs]: {},
      [FunctionalArea.figma_plugin]: {},
      [FunctionalArea.webhook]: {},
    },
    [FunctionalArea.sdk]: {},
    [FunctionalArea.api]: {
      [FunctionalArea.api_keys]: {},
      [FunctionalArea.api_general_apis]: {},
      [FunctionalArea.api_automated_scans]: {},
      [FunctionalArea.api_manual_evaluations]: {},
      [FunctionalArea.api_tasks]: {},
      [FunctionalArea.api_user_flows]: {},
      [FunctionalArea.api_tenant_admin]: {},
      [FunctionalArea.api_pages]: {},
      [FunctionalArea.api_projects]: {},
      [FunctionalArea.api_workspace_users]: {},
      [FunctionalArea.api_scim]: {},
      [FunctionalArea.api_mobile_xml_scans]: {},
      [FunctionalArea.api_mobile_odt_scans]: {},
    },
    // mcp is a top-level sibling of api (not nested under it) because api_manual_evaluations is too broad for MCP read
    // access — it also gates Rule Libraries, Test Guides, AI rule-matching, manual-audit CRUD, and Test Accelerator's
    // Evaluations tab — so mcp_manual_evaluations exists as its own narrowly-scoped area. It's currently the only
    // mcp_* area; other MCP routes are still gated by their api_* areas, so no other mcp_* area is expected here yet.
    [FunctionalArea.mcp]: {
      [FunctionalArea.mcp_manual_evaluations]: {},
    },
    [FunctionalArea.translations]: {},
    [FunctionalArea.evaluation_rules]: {},
    [FunctionalArea.master_library]: {},
    [FunctionalArea.scan_tags]: {},
    [FunctionalArea.active_scans]: {},
    [FunctionalArea.project_management]: {
      [FunctionalArea.pm_projects]: {},
      [FunctionalArea.pm_tasks]: {},
      [FunctionalArea.pm_task_comments]: {},
      [FunctionalArea.pm_task_admin]: {},
    },
    [FunctionalArea.pages]: {},
    [FunctionalArea.automated_flows]: {},
    [FunctionalArea.manual_testing]: {
      [FunctionalArea.mt_manual_evaluations]: {},
      [FunctionalArea.mt_manual_audits]: {},
      [FunctionalArea.mt_mobile_manual_evaluations]: {},
      [FunctionalArea.mt_mobile_manual_evaluation_approvals]: {},
      [FunctionalArea.mt_comments]: {},
      [FunctionalArea.mt_comments_admin]: {},
      [FunctionalArea.mt_update_standards]: {},
      [FunctionalArea.mt_manual_evaluation_review]: {
        [FunctionalArea.mt_manual_finding_reviews]: {},
      },
      [FunctionalArea.mt_auto_vpat_generation]: {},
      [FunctionalArea.mt_auto_vpat_full_generation]: {},
    },
    [FunctionalArea.documents]: {},
    [FunctionalArea.automated_testing]: {
      [FunctionalArea.at_scans]: {},
      [FunctionalArea.at_scanning_agent]: {},
      [FunctionalArea.at_run_automated_scans]: {},
      [FunctionalArea.at_mobile_scans]: {},
      [FunctionalArea.at_mobile_apps]: {},
      [FunctionalArea.at_mobile_odt_scans]: {},
      [FunctionalArea.at_mobile_emulator_scans]: {},
      [FunctionalArea.at_debug_automated_scans]: {},
      [FunctionalArea.at_mobile_xml_scans]: {},
    },
    [FunctionalArea.automated_remediation]: {
      [FunctionalArea.art_scans_and_monitoring]: {},
    },
    [FunctionalArea.ai_summaries]: {
      [FunctionalArea.ai_summaries_in_monitoring]: {},
    },
    [FunctionalArea.ai_triaging]: {
      [FunctionalArea.ai_triaging_in_scans]: {},
    },
    [FunctionalArea.component_matching]: {},
    [FunctionalArea.groups]: {
      [FunctionalArea.user_groups]: {},
      [FunctionalArea.staff_groups]: {},
    },
    [FunctionalArea.tenant_switcher]: {},
    [FunctionalArea.browser_extension]: {
      [FunctionalArea.be_automated_test]: {},
      [FunctionalArea.be_manual_test]: {},
      [FunctionalArea.be_user_flows_api]: {},
    },
    [FunctionalArea.global_users]: {
      [FunctionalArea.gu_global_users]: {},
      [FunctionalArea.gu_all_tenant_admin]: {},
    },
    [FunctionalArea.notifications]: {},
    [FunctionalArea.user_management]: {
      [FunctionalArea.um_user_administration]: {},
      [FunctionalArea.um_organization_administration]: {},
    },
    [FunctionalArea.tenants]: {},
    [FunctionalArea.workspaces]: {},
    [FunctionalArea.digital_properties]: {},
    [FunctionalArea.academy]: {},
    [FunctionalArea.service]: {
      [FunctionalArea.service_support]: {},
      [FunctionalArea.validation_testing]: {},
      [FunctionalArea.ai_chat]: {},
      [FunctionalArea.ai_knowledge_base_search]: {},
      [FunctionalArea.request_manual_evaluation]: {},
    },
    [FunctionalArea.preprod_testing]: {
      [FunctionalArea.preprod_testing_env_settings]: {},
      [FunctionalArea.preprod_testing_reporting]: {},
      [FunctionalArea.preprod_testing_scan_results]: {},
    },
    [FunctionalArea.tenant_rule_libraries]: {
      [FunctionalArea.trl_custom_rule_library_management]: {},
    },
    [FunctionalArea.organization_portfolio]: {},
    [FunctionalArea.program_portfolio]: {},
    [FunctionalArea.organization_details]: {},

    [FunctionalArea.userway]: {
      [FunctionalArea.userway_integration]: {},
    },

    [FunctionalArea.severities]: {
      [FunctionalArea.custom_severities]: {},
    },

    [FunctionalArea.common_findings]: {
      [FunctionalArea.cf_common_findings]: {},
      [FunctionalArea.cf_common_findings_tags]: {},
    },

    [FunctionalArea.artificial_intelligence]: {
      [FunctionalArea.ai_features]: {},
      [FunctionalArea.ai_mobile_crawling]: {},
    },
    [FunctionalArea.reporting_agent]: {},
    [FunctionalArea.golden_dataset]: {},
    [FunctionalArea.admin_portal]: {},
  },

  invalid: {},
});

export const FlattenedFunctionalAreaHierarchy: Record<FunctionalArea, FunctionalArea[]> = SharedCommonUtility.freezeDeep(
  SharedObjectUtility.flattenWithLeafProperties(FunctionalAreaHierarchy),
);
